import React, { useContext, useEffect, useMemo, useState } from 'react';
import Page from '../components/Page';
import { useNavigate, useParams } from 'react-router-dom';
import ProjectMenu, { IMenuItem } from '../components/ProjectMenu';
import '../localization/config';
import { useTranslation } from 'react-i18next';
import { ProjectContext } from '../providers/ProjectProvider';
import { ProjectOverviewProvider } from '../providers/ProjectOverviewProvider';
import ProjectOverview from './ProjectOverview';
import { TeamMembersProvider } from '../providers/TeamMembersProvider';
import TeamMembers from './TeamMembers';
import { ResourcesProvider } from '../providers/ResourcesProvider';
import Resources from './Resources';
import { ActivityProvider } from '../providers/ActivityProvider';
import Activity from './Activity';
import { SettingsProvider } from '../providers/SettingsProvider';
import Settings from './Settings';
import { AuthenticationContext } from '../providers/AuthenticationProvider';

export interface IProject {}

const Project = (_props: IProject) => {
  const { projectData } = useContext(ProjectContext);
  const { systemAdmin, projectManager } = useContext(AuthenticationContext);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tab, parentId, childId } = useParams();

  const [menuItems, setMenuItems] = useState<IMenuItem[]>([]);

  useEffect(() => {
    if (!tab) {
      navigate('overview');
    }
  }, [navigate, tab]);

  useEffect(() => {
    let menuArr: string[] = ['overview', 'resources', 'activity', 'team'];

    if (systemAdmin || projectManager) {
      menuArr.push('settings');
    }

    const menu = menuArr.map((item) => {
      return {
        name: item,
        label: t(`project.menu.${item}`),
        active: tab === item,
      };
    });

    setMenuItems(menu);
  }, [tab, t, projectManager, systemAdmin]);

  const handleMenuClick = (name: string) => {
    navigate(`/project/${projectData?.projectId}/${name}`);
  };

  const crumbs = useMemo(() => {
    const baseCrumbs = [
      {
        key: 'overview',
        route: '/',
        content: t('home.projects'),
        active: false,
        onClick: () => {
          navigate('/');
        },
      },
      {
        key: 'project',
        route: `/project/${projectData?.projectId}`,
        content: projectData?.projectName ? projectData?.projectName : '',
        active: !parentId,
        onClick: () => {
          navigate(`/project/${projectData?.projectId}/overview`);
        },
      },
      {
        key: 'resource',
        route: `/project/${projectData?.projectId}/resources/${parentId}`,
        content: parentId || '',
        active: !parent,
        onClick: () => {
          navigate(`/project/${projectData?.projectId}/resources/${parentId}`);
        },
      },
      {
        key: 'resourceChild',
        route: `/project/${projectData?.projectId}/resources/${parentId}/${childId}`,
        content: childId || '',
        active: true,
      },
    ];

    if (parentId && childId) {
      return baseCrumbs;
    } else if (parentId) {
      return baseCrumbs.slice(0, 3);
    } else {
      return baseCrumbs.slice(0, 2);
    }
  }, [
    childId,
    navigate,
    parentId,
    projectData?.projectId,
    projectData?.projectName,
    t,
  ]);

  return (
    <Page crumbs={crumbs}>
      <ProjectMenu items={menuItems} onClick={handleMenuClick} />
      {tab === 'overview' && (
        <ProjectOverviewProvider>
          <ProjectOverview />
        </ProjectOverviewProvider>
      )}
      {tab === 'team' && (
        <TeamMembersProvider projectId={projectData?.projectId as number}>
          <TeamMembers />
        </TeamMembersProvider>
      )}
      {tab === 'resources' && (
        <TeamMembersProvider projectId={projectData?.projectId as number}>
          <ResourcesProvider projectId={projectData?.projectId as number}>
            <Resources parentId={parentId} childId={childId} />
          </ResourcesProvider>
        </TeamMembersProvider>
      )}
      {tab === 'activity' && (
        <ActivityProvider projectId={projectData?.projectId as number}>
          <Activity />
        </ActivityProvider>
      )}
      {tab === 'settings' && (projectManager || systemAdmin) && (
        <SettingsProvider projectId={projectData?.projectId as number}>
          <Settings />
        </SettingsProvider>
      )}
    </Page>
  );
};

export default Project;
